import { useCallback, useState } from 'react'

/**
 * Ein Stück Oberflächenzustand, das eine Sitzung überlebt – im `localStorage`.
 *
 * Gebraucht für die Filterwahl im Tages-Popup: Konzept Kap. 4.6 verlangt
 * ausdrücklich, dass sie gemerkt wird, und AP 2.4 macht es zum Abnahmekriterium.
 * Wer jeden Morgen im Daily denselben Filter neu setzt, klickt ihn nach einer
 * Woche nicht mehr.
 *
 * **Bewusst nicht serverseitig.** Das ist eine Ansichtsvorliebe je Gerät, keine
 * Einstellung – sie braucht keine Tabelle, keine Migration und keine Synchronität
 * zwischen zwei Rechnern.
 *
 * Fehlender oder unlesbarer Speicher (privates Fenster, abgeschaltete Cookies,
 * alter Wert nach einer Umbenennung) fällt still auf den Standardwert zurück:
 * eine gemerkte Filterwahl ist nichts, wofür eine Ansicht kaputtgehen darf.
 */
export function useMerker<T extends string>(
  schluessel: string,
  standard: T,
  erlaubt: readonly T[],
): [T, (wert: T) => void] {
  const [wert, setWert] = useState<T>(() => {
    try {
      const gelesen = window.localStorage.getItem(`netcon-work:${schluessel}`)
      return gelesen && (erlaubt as readonly string[]).includes(gelesen) ? (gelesen as T) : standard
    } catch {
      return standard
    }
  })

  const setzen = useCallback(
    (neu: T) => {
      setWert(neu)
      try {
        window.localStorage.setItem(`netcon-work:${schluessel}`, neu)
      } catch {
        // Nicht speichern können ist kein Grund, den Filter nicht umzustellen.
      }
    },
    [schluessel],
  )

  return [wert, setzen]
}

/**
 * **Ein gemerkter Wert ohne festen Wertevorrat** – eine Kennung, kein Zustand
 * ([E-327](../../../../docs/ENTSCHEIDUNGEN.md#e-327)).
 *
 * `useMerker` prüft beim Lesen gegen `erlaubt` und schützt so vor einem alten Wert nach einer
 * Umbenennung. Für eine **Aufgabenkennung** geht das nicht: der Vorrat ist die Menge der
 * gerade laufenden Aufgaben, und die steht beim ersten Rendern noch gar nicht fest – sie kommt
 * aus einer Abfrage. Ein `useMerker` mit dieser Liste läse deshalb im ersten Lauf gegen eine
 * leere Menge und fiele jedes Mal auf `null` zurück: gemerkt wäre nichts.
 *
 * Deshalb wird hier **nicht beim Lesen geprüft, sondern beim Benutzen**. Wer den Wert
 * verwendet, weiß, ob die Kennung noch etwas trifft; trifft sie nichts, ist das kein Fehler,
 * sondern eine erledigte Aufgabe. `null` löscht den Eintrag, statt `"null"` zu speichern.
 */
export function useMerkerKennung(
  schluessel: string,
): [string | null, (wert: string | null) => void] {
  const [wert, setWert] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(`netcon-work:${schluessel}`)
    } catch {
      return null
    }
  })

  const setzen = useCallback(
    (neu: string | null) => {
      setWert(neu)
      try {
        if (neu === null) window.localStorage.removeItem(`netcon-work:${schluessel}`)
        else window.localStorage.setItem(`netcon-work:${schluessel}`, neu)
      } catch {
        // Wie oben: nicht speichern können ist kein Grund, die Ansicht nicht umzustellen.
      }
    },
    [schluessel],
  )

  return [wert, setzen]
}

/**
 * Dasselbe für eine **Mehrfachauswahl** – der Filter im Aktivitätsfeed (Nils, 18.08.2026).
 *
 * Getrennt von `useMerker` und nicht als dessen Erweiterung, weil sich der leere Fall
 * unterscheidet: bei einem einzelnen Wert gibt es immer einen Standard, bei einer Menge heißt
 * **leer** „kein Filter" und ist ein gültiger, sogar der übliche Zustand. Ein `useMerker` mit
 * kommagetrennter Zeichenkette hätte die Prüfung gegen `erlaubt` verloren – und damit den
 * Schutz, vor dem der Kommentar oben warnt: ein alter Wert nach einer Umbenennung.
 *
 * Unbekannte Einträge werden beim Lesen **still verworfen**, nicht die ganze Menge. Wird ein
 * Ereignistyp umbenannt, verliert der Filter diesen einen Haken statt aller.
 *
 * ## „Noch nie eingestellt" ist nicht „auf leer gestellt"
 *
 * `standard` gilt **nur, solange nichts gespeichert ist**. Danach zählt das Gespeicherte,
 * auch wenn es leer ist – sonst käme die Voreinstellung nach jedem Neuladen zurück, und
 * „Filter aufheben" wäre ein Knopf, der bis zum nächsten Besuch hält.
 *
 * Nötig wurde die Unterscheidung mit Nils' Wunsch, der Feed möge *„standardmäßig nur
 * Kommentare"* zeigen (18.08.2026): eine Voreinstellung, die man wegklicken kann.
 */
export function useMerkerMenge<T extends string>(
  schluessel: string,
  erlaubt: readonly T[],
  standard: readonly T[] = [],
): [ReadonlySet<T>, (wert: ReadonlySet<T>) => void] {
  const [wert, setWert] = useState<ReadonlySet<T>>(() => {
    try {
      const gelesen = window.localStorage.getItem(`netcon-work:${schluessel}`)
      if (gelesen === null) return new Set<T>(standard)
      const roh: unknown = JSON.parse(gelesen)
      if (!Array.isArray(roh)) return new Set<T>(standard)
      return new Set(roh.filter((e): e is T => (erlaubt as readonly unknown[]).includes(e)))
    } catch {
      return new Set<T>(standard)
    }
  })

  const setzen = useCallback(
    (neu: ReadonlySet<T>) => {
      setWert(neu)
      try {
        window.localStorage.setItem(`netcon-work:${schluessel}`, JSON.stringify([...neu]))
      } catch {
        // Nicht speichern können ist kein Grund, den Filter nicht umzustellen.
      }
    },
    [schluessel],
  )

  return [wert, setzen]
}
